import { useMemo } from 'react';

import { Apollo } from 'src/gql/generated';

import type { GroupableColumn } from './useGroupableColumns';

/**
 * The parts of a report column option the grouping picker reads. Structural,
 * so the `reportColumnOptions` query result can be handed over as-is.
 */
type ReportColumnOptionLike =
  | ({ __typename: 'ExtractionReportColumn' } & Pick<
      Apollo.ExtractionReportColumn,
      'dataType' | 'displayName' | 'isGroupable' | 'questionId'
    >)
  | { __typename: 'FileFieldColumn' };

const UNTITLED_QUESTION_LABEL = 'Untitled';

/**
 * The org's extraction questions as Display Options grouping choices, for a
 * grid that defines no column per question.
 *
 * The report grid gets its question choices from `useGroupableColumns`, since
 * every question a report can group by is a column on it. The repository grid
 * shows no question columns, yet the server groups its rows by any question in
 * the org (a document with no extraction job lands under "(No answer)"), so
 * its picker takes them from the column options directly. The built-in file
 * fields are left out: the grid already supplies those.
 *
 * Sorted by title, the order the columns panel lists questions in, since there
 * is no grid order to keep.
 */
export function getQuestionGroupableColumns(
  reportColumnOptions: readonly ReportColumnOptionLike[]
): GroupableColumn[] {
  return reportColumnOptions
    .flatMap((option): GroupableColumn[] => {
      if (option.__typename !== 'ExtractionReportColumn' || !option.isGroupable) return [];
      return [
        {
          field: option.questionId,
          label: option.displayName || UNTITLED_QUESTION_LABEL,
          // `ReportColumnDataType` values are MUI column types; `createReportGridColDef` relies on the same.
          type: option.dataType,
        },
      ];
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** {@link getQuestionGroupableColumns} over the org's column options. */
export function useQuestionGroupableColumns() {
  const { data, loading } = Apollo.useGetReportColumnOptionsQuery();
  const reportColumnOptions = data?.reportColumnOptions;
  const questionGroupableColumns = useMemo(
    () => (reportColumnOptions ? getQuestionGroupableColumns(reportColumnOptions) : []),
    [reportColumnOptions]
  );

  return { loading, questionGroupableColumns };
}
